/**
 * PushNotification entity class
 */

import moment from 'moment'
import { timeStringToDate, formatDateByTimezone } from 'app/helpers/date'
import { clone, has } from '../../../app/helpers/object'
import { PLATFORM_TYPES } from '../../../app/core/constants'

/**
 * PushNotificationFactory
 * returns PushNotification entity class
 *
 * @class PushNotificationFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.PushNotification} class
 *
 * @ngdoc factory
 * @name mwApp.factory:PushNotificationFactory
 */
/* @ngInject */
export default function PushNotificationFactory ($http, $q, $injector, Entity, alertLiteSvc, storageSvc, accessParser, authSvc, Type) {
  /* === PushNotification class === */

  /**
   * PushNotification model
   *
   * @class PushNotification
   * @classdesc PushNotification model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @return {models.PushNotification} PushNotification entity
   */
  function PushNotification () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  PushNotification.prototype = Object.create(Entity.prototype)
  PushNotification.prototype.constructor = PushNotification

  /* === PushNotification instance methods & fields === */

  Object.assign(PushNotification.prototype, {

    /**
     * instance fields
     *
     * @memberof models.PushNotification#
     * @type {Object}
     */
    _fields: {
      id: String,
      networkId: Number,
      name: String,
      createdTime: moment,
      updatedTime: moment,
      createdBy: String,
      updatedBy: String,
      status: String,
      title: String,
      description: String,
      resourceType: String,
      contentType: String,
      $contentType: String,
      content: { name: String, id: Number },
      $subscription: 'Subscription',
      subscriptionId: Number,
      subscriptionName: String,
      contentEnabled: Boolean,
      contentUnassignedOrDeleted: Boolean,
      $platforms: Type.CSV(';'),
      platformTypes: [],
      subscriberStatuses: [],
      $subscriberStatuses: [],
      scheduleType: String,
      startTime: moment,
      $startDate: moment,
      $startTime: moment,
      $isCopy: false
    },

    /**
     * url template
     *
     * @memberof models.PushNotification#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/push-notifications/{{::id}}',

    /**
     * Get underlying object
     *
     * @memberof models.PushNotification#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      this.contentType = this.$contentType ? this.$contentType.value : null

      this.platformTypes = this.$platforms.filter(platform => platform.checked).map(platform => platform.name.toUpperCase())
      this.subscriberStatuses = this.$subscriberStatuses.filter(status => status.checked).map(status => status.value)

      if (this.scheduleType === 'SCHEDULED') {
        const startDate = this.$startDate.setHours(this.$startTime.getHours(), this.$startTime.getMinutes())
        this.startTime = formatDateByTimezone(startDate).format('YYYY-MM-DDTHH:mm:ss[Z]')
      } else {
        this.startTime = null
      }
      this.subscriptionId = this.$subscription.id

      const res = angular.copy(this)
      Object.keys(this).forEach((key) => {
        if (key.indexOf('$') === 0 || key.indexOf('_') === 0) {
          delete res[key]
        }
      })
      return res
    },
    /**
     * Get underlying object
     *
     * @memberof models.PushNotification#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _prepare: function () {
      this.$startDate = this.startTime ? formatDateByTimezone(this.startTime, false).toDate() : moment().toDate()
      this.$startTime = timeStringToDate(this.startTime ? moment.utc(this.startTime).format('HH:mm') : null, 'startOfDay')
    },
    _isNew: function () {
      return !this.id
    },
    /**
     * Get platforms array with checked flags, depending on network platforms array
     *
     * @memberof models.FeaturedCarousel#
     * @return {Object[]}
     */
    _enhancePlatforms: function (def) {
      var ret = clone(PLATFORM_TYPES.filter((p) => {
        return !(!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED') && p.name === 'WEB')
      }))

      if (this._isNew() && def && !this.$isCopy) {
        for (let i = ret.length; i--;) {
          if (def.filter(item => item.toLowerCase() === ret[i].name.toLowerCase()).length) {
            ret[i].checked = true
          }
        }
      } else
      if (this.platformTypes && this.platformTypes.length) {
        for (let i = ret.length; i--;) {
          ret[i].checked = this.platformTypes.indexOf(ret[i].name.toUpperCase()) !== -1
        }
      }
      return ret
    },
    /**
     * Get platforms array with checked flags, depending on network platforms array
     *
     * @memberof models.FeaturedCarousel#
     * @return {Object[]}
     */
    _enhanceStatuses: function (def) {
      const ret = [
        { name: 'Active', value: 'ACTIVE' },
      ]

      if (this._isNew() && def && !this.$isCopy) {
        for (let i = ret.length; i--;) {
          if (def.filter(item => item.toLowerCase() === ret[i].value.toLowerCase()).length) {
            ret[i].checked = ['ON_HOLD', 'ACTIVE'].includes(ret[i].value)
          }
        }
      } else
      if (this.subscriberStatuses && this.subscriberStatuses.length) {
        for (let i = ret.length; i--;) {
          ret[i].checked = this.subscriberStatuses.indexOf(ret[i].value) !== -1
        }
      }
      return ret
    }

  })

  PushNotification.loadSmartPN = function (tableState, o) {
    o = o || {}

    var cast = $injector.get(this._name) // get constructor from injector by name
    let url = cast.prototype.$_urlTemplate(o)

    // if there is access defined in entity class -> check 'view' access
    if (cast._name && cast.access && cast.access.view && !accessParser(cast.access.view).check()) {
      // return empty array for smart table in case user doesn't have 'view' access
      return $q.when({ content: [] })
    }

    // {"sort":{"predicate":"channelNumber","reverse":false},"search":{},"pagination":{"start":0,"totalItemCount":0}}
    // sort-by=channelNumber&sort-order=asc&page=1&count=25&type=TV
    var params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number
    }
    Object.assign(params, tableState.search.predicateObject)
    if (params.$) {
      params.q = params.$
      delete params.$
    }
    if (params.created) {
      params['created-from'] = JSON.parse(params.created).after
      params['created-to'] = JSON.parse(params.created).before
      delete params.created
    }
    if (params.updated) {
      params['updated-from'] = JSON.parse(params.updated).after
      params['updated-to'] = JSON.parse(params.updated).before
      delete params.updated
    }
    if (params.modifiedBy) {
      params['modified-by'] = params.modifiedBy
      delete params.modifiedBy
    }
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url

    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          var result = this.cast(data, {
            // response structure
            content: [cast._name],
            first: true,
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            sort: [{
              ascending: true,
              direction: '',
              ignoreCase: true,
              nullHandling: '',
              property: ''
            }],
            totalElements: 0,
            totalPages: 0
          })

          // change SmartTable state
          if (Array.isArray(result.sort) && result.sort.length) {
            var last = result.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = result.number * result.size // first element
          tableState.pagination.number = result.size // page size
          tableState.pagination.numberOfPages = result.totalPages
          tableState.pagination.totalItemCount = result.totalElements

          // return array for SmartTable
          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  /**
   * Search content by param
   *
   * @param {String} url
   * @param {Object} params
   * @returns {Promise} promise
   */
  PushNotification.search = function (url, params) {
    return $http.get(url, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /* === PushNotification static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(PushNotification, Entity)
  PushNotification._name = 'PushNotification'
  PushNotification._publicName = 'Push Notifications'
  PushNotification.init()

  return PushNotification
}
